import type {
  AvailabilityMap,
  AvailabilityStatus,
  DateAvailabilityMap,
  WeeklyScheduleToday,
} from '../types';

/**
 * Resolves the effective availability status for a contributor+solution on a given date.
 *
 * Priority order (highest to lowest):
 *   1. weeklyScheduleToday / weekly schedule entry for the date
 *   2. Active dateAvailability range covering the date
 *   3. Base availability (qualification level)
 */
export function resolveEffectiveStatus(
  date: string,
  contributorId: string,
  solutionId: string,
  weeklyScheduleForDate: WeeklyScheduleToday,
  dateAvailability: DateAvailabilityMap,
  baseAvailability: AvailabilityMap
): AvailabilityStatus | undefined {
  // 1. Weekly schedule override
  const daySchedule = weeklyScheduleForDate[contributorId];
  if (daySchedule && daySchedule[solutionId] !== undefined) {
    return daySchedule[solutionId];
  }

  // 2. Active date-range override
  const dateRanges = dateAvailability[contributorId]?.[solutionId];
  if (dateRanges) {
    const active = dateRanges.find(r => r.startDate <= date && r.endDate >= date);
    if (active) return active.status;
  }

  // 3. Base availability
  return baseAvailability[contributorId]?.[solutionId];
}

/**
 * Builds the full effective availability map for today, applying the three-level priority chain.
 * Used by the today view in Dashboard.tsx.
 */
export function buildEffectiveAvailabilityForToday(
  today: string,
  baseAvailability: AvailabilityMap,
  dateAvailability: DateAvailabilityMap,
  weeklyScheduleToday: WeeklyScheduleToday
): AvailabilityMap {
  const effective: AvailabilityMap = {};

  // Collect all contributor IDs across all maps
  const allContributorIds = new Set<string>([
    ...Object.keys(baseAvailability),
    ...Object.keys(dateAvailability),
    ...Object.keys(weeklyScheduleToday),
  ]);

  for (const cId of allContributorIds) {
    const allSolutionIds = new Set<string>([
      ...Object.keys(baseAvailability[cId] ?? {}),
      ...Object.keys(dateAvailability[cId] ?? {}),
      ...Object.keys(weeklyScheduleToday[cId] ?? {}),
    ]);

    for (const sId of allSolutionIds) {
      const status = resolveEffectiveStatus(
        today,
        cId,
        sId,
        weeklyScheduleToday,
        dateAvailability,
        baseAvailability
      );
      if (status !== undefined) {
        if (!effective[cId]) effective[cId] = {};
        effective[cId][sId] = status;
      }
    }
  }

  return effective;
}
